import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Loader2, ArrowUpRight, ArrowDownRight, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCreditBalance, getCreditTransactions } from "@/lib/api";

const TYPE_LABEL = {
  credit: "Kredit Masuk",
  debit: "Pemakaian",
  reserve: "Reservasi",
  release: "Pelepasan",
  refund: "Pengembalian",
  adjustment: "Penyesuaian",
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function Credits() {
  const [balance, setBalance] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getCreditBalance(), getCreditTransactions()])
      .then(([b, t]) => {
        setBalance(b.credit_balance);
        setTxns(t.transactions || []);
      })
      .catch(() => toast.error("Gagal memuat kredit"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Kredit</p>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Kredit Saya</h1>
      </div>

      <div className="rounded-2xl bg-blue-600 p-8 text-white" data-testid="credit-balance-card">
        <div className="flex items-center gap-2 text-blue-100">
          <Coins className="h-5 w-5" /> Saldo Kredit
        </div>
        <p className="mt-2 font-heading text-5xl font-bold" data-testid="credit-balance-value">
          {balance === null ? "—" : balance} <span className="text-2xl font-medium text-blue-100">Kredit</span>
        </p>
        <Link to="/beli-kredit" data-testid="credits-buy-btn" className="mt-5 inline-block">
          <Button className="rounded-full bg-white px-6 text-blue-700 hover:bg-blue-50 active:scale-95 transition-transform">
            <Wallet className="mr-2 h-4 w-4" /> Beli Kredit
          </Button>
        </Link>
      </div>

      <div className="space-y-4">
        <h2 className="font-heading text-lg font-semibold text-slate-900">Riwayat Kredit</h2>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" /> Memuat...
          </div>
        ) : txns.length === 0 ? (
          <div data-testid="credit-history-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="font-heading font-semibold text-slate-900">Belum ada transaksi kredit.</p>
            <p className="mt-1 text-sm text-slate-500">Riwayat penggunaan kredit Anda akan muncul di sini.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="credit-history-list">
            {txns.map((t) => {
              const positive = t.amount >= 0;
              return (
                <div key={t.id} className="flex items-center gap-4 px-5 py-4">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-full ${positive ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                    {positive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">{TYPE_LABEL[t.type] || t.type}</p>
                    <p className="text-xs text-slate-400">{formatDate(t.created_at)} · {t.status}</p>
                  </div>
                  <span className={`font-semibold ${positive ? "text-emerald-600" : "text-amber-600"}`}>
                    {positive ? "+" : ""}{t.amount}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
